import * as angular from 'angular';
import { TimerBase } from '../timer';
import { v4 as uuid } from 'uuid';
import {
	EVENTS as MediaPlayerEvents,
	STATES as MediaPlayerStates
} from '../media-player';
import { debounce } from '@go-modules/common/helpers/utils';

/**
 * Player synchronization service
 *
 * For a given set of tracks, synchronize their
 * individual playback to conform to a set of
 * sync events (play, pause, or seek).
 *
 * TODO: support remote recorder (recording over a network) that has same interface as recorder
 */

/* @ngInject */
export const PlayerSync = function ($q, $log, EventChannel, Timer, PlayerSyncTrack) {

	// Current auto-generated track number
	let autoTrackNumber = 1000;

	// Mirror track capture modes
	playerSync.MODE = PlayerSyncTrack.MODE;

	// Supported events
	playerSync.EVENT = {
		SYNC_EVENT_ADDED: 'syncEventAdded',
		SYNC_EVENT_UPDATED: 'syncEventUpdated',
		SYNC_EVENT_REMOVED: 'syncEventRemoved'
	};

	/**
	 * Player Sync service
	 *
	 * @param timer Timer instance that drives all synchronization
	 * @constructor
	 */
	function playerSync (timer) {
		const vm = this;
		const deferred = $q.defer();
		const eventChannel = new EventChannel();

		// List of tracks
		this.tracks = [];

		// Event publishing / subscribing
		this.trigger = eventChannel.broadcast.bind(eventChannel);
		this.on = eventChannel.subscribe.bind(eventChannel);
		this.once = eventChannel.subscribeOnce.bind(eventChannel);
		this.has = eventChannel.hasSubscriber.bind(eventChannel);
		this.off = eventChannel.unsubscribe.bind(eventChannel);

		// Set the current master timer
		timer = timer || Timer.get('master-timer-' + uuid());
		this.setMasterTimer(timer);

		this.$resolved = false;
		this.$promise = deferred.promise;

		// Wait for the master timer to be ready.
		// We should know timer duration when it is ready.
		this.masterTimer.$promise.then(function () {
			vm.$resolved = true;
			deferred.resolve(vm);
		});
	}

	/**
	 * Generates a track number
	 *
	 * @returns Number
	 */
	playerSync.generateTrackNumber = function () {
		return autoTrackNumber++;
	};

	/**
	 * Set the current master timer
	 *
	 * Note: the only reason we have to do this is
	 * because we lose a reference to the correct
	 * master timer instance. When we stop wrapping
	 * the session player in a timer instance and use
	 * a plain javascript timer, then we can won't
	 * need to ever update the timer reference.
	 *
	 * @param timer Master timer instance
	 */
	playerSync.prototype.setMasterTimer = function (timer) {
		const vm = this;

		// For time subscriptions
		let _lastTimeReached = -1;

		if (!(timer instanceof Timer) && !(timer instanceof TimerBase)) {
			throw new Error('Must update timer with a valid timer instance');
		}

		// If there is a previous master timer, destroy it
		if (this.masterTimer) {
			this.masterTimer.destroy();
		}

		// Overwrite the current master timer with this one
		this.masterTimer = timer;
		this.masterTimer.$promise = this.masterTimer.$promise || $q.when(this.masterTimer);

		// Convert timer events to player events
		this.masterTimer.on(Timer.EVENT.START, function () {
			vm.trigger(MediaPlayerEvents.PLAY);
		});
		this.masterTimer.on(Timer.EVENT.PAUSE, function () {
			vm.trigger(MediaPlayerEvents.PAUSE);
		});
		this.masterTimer.on(Timer.EVENT.TIME, function (time) {
			vm.trigger(MediaPlayerEvents.TIME, {
				position: time,
				duration: vm.getDuration()
			});

			// Check for time subscribers
			if (hasTimeSubscriber(time)) {
				vm.trigger(Math.floor(time / 1000), time);
			}
		});
		this.masterTimer.on(Timer.EVENT.TIME_UPDATE, function () {
			vm.trigger(MediaPlayerEvents.REQUEST_SEEK, {});
			vm.trigger(MediaPlayerEvents.SEEK, {});
		});
		this.masterTimer.on(Timer.EVENT.COMPLETE, function () {
			vm.trigger(MediaPlayerEvents.COMPLETE);
		});

		/**
		 * Whether there is a event subscriber for the specific time
		 *
		 * @param time
		 * @returns {Boolean}
		 */
		function hasTimeSubscriber (time) {
			time = Math.floor(time / 1000); // convert to seconds
			if (parseInt(`${_lastTimeReached}`, 10) === parseInt(time, 10)) {
				return false;
			}
			_lastTimeReached = time;
			return vm.has(time);
		}
	};

	/**
	 * Whether player sync is ready for playback
	 *
	 * @returns {boolean}
	 */
	playerSync.prototype.isReady = function () {
		return this.tracks.every((track) => track.$resolved) && this.$resolved;
	};

	/**
	 * Returns a single promise that will resolve when all
	 * registered tracks and the master timer are ready.
	 *
	 * @returns {Promise}
	 */
	playerSync.prototype.getReady = function () {
		return $q.all([
			this.masterTimer.$promise
		].concat(this.tracks.map(function (track) {
			return track.$promise;
		})));
	};

	/**
	 * Add a track
	 *
	 * @param trackNumber
	 * @param player
	 * @param syncEvents
	 * @returns {PlayerSyncTrack}
	 */
	playerSync.prototype.addTrack = function (trackNumber, player, syncEvents) {
		const vm = this;

		// First check to see if the track has already been added
		if (this.getTrackByNumber(trackNumber)) {
			throw new Error('Track has already been added');
		}

		const track = new PlayerSyncTrack(trackNumber, vm.masterTimer, player, syncEvents);

		// This debounced function ensures that the master timer
		// doesn't receive simultaneous play / pause requests.
		// When it does, we end up getting undesired behavior.
		// i.e. the master timer plays instead of pauses. This
		// will only be a problem when the timer is a player underneath.
		const validatePlayback = debounce(function (pause) {
			if (pause) {
				vm.masterTimer.pause();
			} else {
				vm.masterTimer.start();
			}
		});

		// Add the new track
		vm.tracks.push(track);

		// Handles sync event added events
		track.on(PlayerSyncTrack.EVENT.SYNC_EVENT_ADDED, function (syncEvent) {
			vm.trigger(playerSync.EVENT.SYNC_EVENT_ADDED, syncEvent);
		});

		// Handles sync event updated events
		track.on(PlayerSyncTrack.EVENT.SYNC_EVENT_UPDATED, function (syncEvent) {
			vm.trigger(playerSync.EVENT.SYNC_EVENT_UPDATED, syncEvent);
		});

		// Handles sync event removed events
		track.on(PlayerSyncTrack.EVENT.SYNC_EVENT_REMOVED, function (syncEvent) {
			vm.trigger(playerSync.EVENT.SYNC_EVENT_REMOVED, syncEvent);
		});

		// Handles sync event executed events
		track.on(PlayerSyncTrack.EVENT.SYNC_EVENT_EXECUTED, function () {
			vm.validate(track); // Sync other tracks
		});

		// Handles track interrupt events
		track.on(PlayerSyncTrack.EVENT.INTERRUPT, function () {
			$log.info('INTERRUPT!', track.number);

			// Ensure master timer is paused
			if (!vm.masterTimer.isPaused()) {
				validatePlayback(true);
			}
		});

		// Handles track interrupt complete events
		track.on(PlayerSyncTrack.EVENT.INTERRUPT_COMPLETE, function () {
			$log.info('INTERRUPT COMPLETE!', track.number);

			// Get the other tracks
			const otherTracks = getOtherTracks(track, vm.tracks);

			// If other tracks are still interrupted, pause the
			// master timer, if not, then resume playback as normal.
			validatePlayback(numTracksInterrupted(otherTracks) > 0);
		});

		// Log track information for debugging purposes
		track.$promise.then(function () {
			$log.debug(track.getInfo());
		});

		return track;
	};

	/**
	 * Remove track
	 *
	 * @param track
	 * @returns {Number}
	 */
	playerSync.prototype.removeTrack = function (track) {
		// Destroy the track
		track.destroy();

		// Remove it from the list of tracks
		const index = this.tracks.indexOf(track);
		if (index !== -1) {
			this.tracks.splice(index, 1);
		}

		return index;
	};

	/**
	 * Returns a track by number
	 *
	 * @returns PlayerSyncTrack
	 */
	playerSync.prototype.getTrackByNumber = function (number) {
		const filteredTracks = this.tracks.filter(function (track) {
			return track.number === number;
		});
		return filteredTracks.length > 0 ? filteredTracks[0] : null;
	};

	/**
	 * Toggle all `track.player` captions on / off
	 *
	 * @param enable
	 */
	playerSync.prototype.toggleCaptions = function (enable) {
		this.tracks.forEach((track) => {
			track.player.toggleCaptions?.(enable);
		});
		if (this.masterTimer.hasCaptions?.()) {
			this.masterTimer.toggleCaptions?.(enable);
		}
	};


	/**
	 * Whether or not at least one `track.player` has captions
	 *
	 * @returns {boolean}
	 */
	playerSync.prototype.hasCaptions = function () {
		return this.tracks.some((track) => angular.isFunction(track.player.hasCaptions)
			&& track.player.hasCaptions()) || this.masterTimer.hasCaptions?.() === true;
	};

	/**
	 * Whether or not at least one `track.player` has captions enabled
	 *
	 * @returns {boolean}
	 */
	playerSync.prototype.captionsEnabled = function () {
		return this.tracks.some((track) => angular.isFunction(track.player.captionsEnabled)
			&& track.player.captionsEnabled()) || this.masterTimer.captionsEnabled?.() === true;
	};

	/**
	 * Add sync event
	 *
	 * @param syncEvent Sync event instance
	 * @param suppress Suppress event broadcast
	 */
	playerSync.prototype.addSyncEvent = function (syncEvent, suppress) {
		const track = this.getTrackByNumber(syncEvent.track_number);

		if (!track) {
			throw new Error('Track does not exist');
		}

		track.addSyncEvent(syncEvent, suppress);
	};

	/**
	 * Remove sync event
	 *
	 * @param syncEvent Sync event instance
	 * @param suppress Suppress event broadcast
	 */
	playerSync.prototype.removeSyncEvent = function (syncEvent, suppress) {
		const track = this.getTrackByNumber(syncEvent.track_number);

		if (!track) {
			throw new Error('Track does not exist');
		}

		track.removeSyncEvent(syncEvent, suppress);
	};

	/**
	 * Set the mode for all tracks
	 *
	 * @param mode
	 */
	playerSync.prototype.setMode = function (mode) {
		angular.forEach(this.tracks, function (track) {
			track.setMode(mode);
		});
	};

	/**
	 * Whether one of the supported capture modes are enabled for all tracks
	 *
	 * @returns {boolean}
	 */
	playerSync.prototype.isCaptureMode = function () {
		if (!this.tracks.length) {
			return false;
		}
		const values = this.tracks.map(function (track) {
			return track.isCaptureMode();
		});
		return values.indexOf(false) === -1;
	};

	/**
	 * Validate all tracks.
	 * When a track is supplied, all tracks except
	 * the supplied one will perform validation.
	 *
	 * @param ignoreTrack
	 */
	playerSync.prototype.validate = function (ignoreTrack) {
		angular.forEach(this.tracks, function (track) {
			if (ignoreTrack !== track) {
				track.validate(true);
			}
		});
	};

	/**
	 * Play player sync
	 */
	playerSync.prototype.play = function () {
		const vm = this;
		vm.getReady().then(function () {
			vm.masterTimer.start();
		});
	};

	/**
	 * Pause player sync
	 */
	playerSync.prototype.pause = function () {
		const vm = this;
		vm.getReady().then(function () {
			vm.masterTimer.pause();
		});
	};

	/**
	 * Seek to a specific time (in milliseconds)
	 *
	 * @param time
	 */
	playerSync.prototype.seek = function (time) {
		const vm = this;
		vm.getReady().then(function () {
			vm.masterTimer.setTime(time);
		});
	};

	/**
	 * Stop player sync
	 */
	playerSync.prototype.stop = function () {
		const vm = this;
		vm.getReady().then(function () {
			vm.seek(0);
			vm.pause();
		});
	};

	/**
	 * Whether player sync is paused
	 *
	 * @returns {boolean}
	 */
	playerSync.prototype.isPaused = function () {
		return this.masterTimer.isPaused();
	};

	/**
	 * Whether player sync is playing
	 *
	 * @returns {boolean}
	 */
	playerSync.prototype.isPlaying = function () {
		return !this.masterTimer.isPaused();
	};

	/**
	 * Whether player sync is finished playing
	 *
	 * @returns {boolean}
	 */
	playerSync.prototype.isComplete = function () {
		return this.masterTimer.isComplete();
	};

	/**
	 * Whether player sync is buffering
	 *
	 * @returns {boolean}
	 */
	playerSync.prototype.isBuffering = function () {
		return numTracksInterrupted(this.tracks) > 0;
	};

	/**
	 * Get master time
	 *
	 * @returns {number}
	 */
	playerSync.prototype.getTime = function () {
		return this.masterTimer.getTime();
	};

	/**
	 * Get master time
	 *
	 * @returns {number}
	 */
	playerSync.prototype.getDuration = function () {
		return this.masterTimer.getDuration();
	};

	/**
	 * Get master time
	 *
	 * @returns {string}
	 */
	playerSync.prototype.getState = function () {
		return this.isPaused() ? MediaPlayerStates.PAUSED : MediaPlayerStates.PLAYING;
	};

	playerSync.prototype.isPlaybackRateSupported = () => {
		return false;
	};

	/**
	 * Get master time
	 *
	 * @returns {number}
	 */
	playerSync.prototype.getProvider = function () {
		return {name: 'playerSync'};
	};

	/**
	 * Destroy
	 */
	playerSync.prototype.destroy = function () {
		const vm = this;
		angular.forEach(this.tracks, function (track) {
			vm.removeTrack(track);
		});
		this.masterTimer.destroy();
		this.off();
	};

	/**
	 * Get all tracks besides the specified track
	 *
	 * @param track
	 * @param tracks
	 * @returns {Array}
	 */
	function getOtherTracks (track, tracks) {
		const otherTracks = [].concat(tracks),
			trackIndex = otherTracks.indexOf(track);
		if (trackIndex >= 0) {
			otherTracks.splice(trackIndex, 1);
		}
		return otherTracks;
	}

	/**
	 * Returns a count of how many players are
	 * buffering in a given set of players.
	 *
	 * @param tracks
	 * @returns {number}
	 */
	function numTracksInterrupted (tracks) {
		let count = 0;
		angular.forEach(tracks, function (track) {
			if (track.$resolved && track.isInterrupted()) {
				count++;
			}
		});

		return count;
	}

	return playerSync;
};
